import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { readingMinutesFor, slugify } from "./site";

function serverPublicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

const ARTICLE_LIST_COLS =
  "id, slug, title, subtitle, excerpt, cover_image_url, cover_image_alt, category_id, published_at, reading_minutes, author_name, is_ai_generated";

export const listPublishedArticles = createServerFn({ method: "GET" })
  .inputValidator((data: { limit?: number; categorySlug?: string } | undefined) =>
    z
      .object({ limit: z.number().int().min(1).max(60).optional(), categorySlug: z.string().optional() })
      .parse(data ?? {}),
  )
  .handler(async ({ data }) => {
    const supabase = serverPublicClient();
    let categoryId: string | null = null;
    let category:
      | { id: string; name: string; slug: string; description: string | null }
      | null = null;
    if (data.categorySlug) {
      const { data: cat } = await supabase
        .from("categories")
        .select("id, name, slug, description")
        .eq("slug", data.categorySlug)
        .maybeSingle();
      if (!cat) return { articles: [], category: null };
      categoryId = cat.id;
      category = cat;
    }
    let q = supabase
      .from("articles")
      .select(ARTICLE_LIST_COLS)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(data.limit ?? 24);
    if (categoryId) q = q.eq("category_id", categoryId);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { articles: rows ?? [], category };
  });

export const listCategories = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = serverPublicClient();
  const { data, error } = await supabase
    .from("categories")
    .select("id, slug, name, description, sort_order")
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getArticleBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => z.object({ slug: z.string().min(1) }).parse(data))
  .handler(async ({ data }) => {
    const supabase = serverPublicClient();
    const { data: article, error } = await supabase
      .from("articles")
      .select("*")
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!article) return null;
    let category = null;
    if (article.category_id) {
      const { data: cat } = await supabase
        .from("categories")
        .select("id, slug, name")
        .eq("id", article.category_id)
        .maybeSingle();
      category = cat;
    }
    return { article, category };
  });

export const listRelatedArticles = createServerFn({ method: "GET" })
  .inputValidator((data: { categoryId: string | null; excludeId: string }) =>
    z.object({ categoryId: z.string().nullable(), excludeId: z.string() }).parse(data),
  )
  .handler(async ({ data }) => {
    const supabase = serverPublicClient();
    let q = supabase
      .from("articles")
      .select(ARTICLE_LIST_COLS)
      .eq("status", "published")
      .neq("id", data.excludeId)
      .order("published_at", { ascending: false })
      .limit(3);
    if (data.categoryId) q = q.eq("category_id", data.categoryId);
    const { data: rows } = await q;
    return rows ?? [];
  });

/* ---------- Admin operations ---------- */

const FaqItem = z.object({
  question: z.string().min(3).max(300),
  answer: z.string().min(3).max(2000),
});

const ArticleUpsert = z.object({
  id: z.string().uuid().optional(),
  title: z.string().min(3).max(200),
  slug: z.string().min(3).max(120).optional(),
  subtitle: z.string().max(300).optional().nullable(),
  excerpt: z.string().max(500).optional().nullable(),
  content_md: z.string().default(""),
  cover_image_url: z.string().url().optional().nullable(),
  cover_image_alt: z.string().max(200).optional().nullable(),
  category_id: z.string().uuid().optional().nullable(),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  author_name: z.string().max(120).optional().nullable(),
  seo_title: z.string().max(120).optional().nullable(),
  seo_description: z.string().max(200).optional().nullable(),
  is_ai_generated: z.boolean().optional(),
  source_urls: z.array(z.string()).optional(),
  focus_keyword: z.string().max(120).optional().nullable(),
  secondary_keywords: z.array(z.string().min(1).max(120)).max(20).optional(),
  key_takeaways: z.array(z.string().min(1).max(400)).max(12).optional(),
  faq: z.array(FaqItem).max(15).optional(),
  entities: z.array(z.string().min(1).max(120)).max(30).optional(),
});


async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

export const adminListArticles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("articles")
      .select(
        "id, slug, title, status, is_ai_generated, category_id, published_at, updated_at, view_count, cover_image_url, cover_image_alt, cover_art_direction",
      )
      .order("updated_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const adminRegenerateCover = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { ensureCoverForArticle } = await import("./cover-image.server");
    const result = await ensureCoverForArticle(data.id, { force: true });
    if (!result) throw new Error("Article not found or cover generation failed");
    return {
      ok: true,
      cover_image_url: result.url,
      image_type: result.direction?.image_type ?? null,
    };
  });

export const adminGetArticle = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("articles")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;

    let idea: any = null;
    const ideaId = (row as any).idea_id as string | null | undefined;
    if (ideaId) {
      const { data: ideaRow } = await context.supabase
        .from("article_ideas")
        .select(
          "id, title, summary, angle, source_url, source_name, source_tier, source_channel, source_published_at, novelty_score, urgency, discovered_at, created_at",
        )
        .eq("id", ideaId)
        .maybeSingle();
      idea = ideaRow ?? null;
    }
    return { ...row, idea } as typeof row & { idea: typeof idea };
  });


export const adminUpsertArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => ArticleUpsert.parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const slug = data.slug || slugify(data.title);
    const reading_minutes = readingMinutesFor(data.content_md ?? "");
    const published_at =
      data.status === "published" ? new Date().toISOString() : null;

    const payload = {
      title: data.title,
      slug,
      subtitle: data.subtitle ?? null,
      excerpt: data.excerpt ?? null,
      content_md: data.content_md ?? "",
      cover_image_url: data.cover_image_url ?? null,
      cover_image_alt: data.cover_image_alt ?? null,
      category_id: data.category_id ?? null,
      status: data.status,
      author_name: data.author_name ?? null,
      seo_title: data.seo_title ?? null,
      seo_description: data.seo_description ?? null,
      is_ai_generated: data.is_ai_generated ?? false,
      source_urls: data.source_urls ?? [],
      focus_keyword: data.focus_keyword ?? null,
      secondary_keywords: data.secondary_keywords ?? [],
      key_takeaways: data.key_takeaways ?? [],
      faq: (data.faq ?? []) as any,
      entities: data.entities ?? [],
      reading_minutes,
      author_id: context.userId,
      ...(data.id ? {} : { published_at }),
    };


    let resultRow: { id: string; slug: string };
    let wasPublishTransition = false;

    if (data.id) {
      // Preserve published_at if already set
      const { data: existing } = await context.supabase
        .from("articles")
        .select("published_at, status")
        .eq("id", data.id)
        .maybeSingle();
      // Guard: only 'published' rows keep a published_at. Any other status
      // clears it so we can never end up with backdated drafts again.
      const finalPublishedAt =
        data.status === "published"
          ? existing?.published_at ?? new Date().toISOString()
          : null;
      wasPublishTransition =
        data.status === "published" && existing?.status !== "published";
      const { data: updated, error } = await context.supabase
        .from("articles")
        .update({ ...payload, published_at: finalPublishedAt })
        .eq("id", data.id)
        .select("id, slug")
        .single();
      if (error) throw new Error(error.message);
      resultRow = updated;
    } else {
      wasPublishTransition = data.status === "published";
      const { data: inserted, error } = await context.supabase
        .from("articles")
        .insert(payload)
        .select("id, slug")
        .single();
      if (error) throw new Error(error.message);
      resultRow = inserted;
    }

    // Converge with the automated newsroom publish path: whenever a save
    // transitions the row into 'published', run the same side effects
    // (cover image ensure + syndication fan-out). Never let these fail the save.
    if (wasPublishTransition) {
      try {
        const { ensureCoverForArticle } = await import("./cover-image.server");
        await ensureCoverForArticle(resultRow.id);
      } catch (err) {
        console.warn(
          `[adminUpsertArticle] cover ensure failed for ${resultRow.id}:`,
          (err as Error).message,
        );
      }
      try {
        const { data: fresh } = await context.supabase
          .from("articles")
          .select("title, excerpt, cover_image_url")
          .eq("id", resultRow.id)
          .maybeSingle();
        const { syndicateArticle } = await import("./syndication.server");
        await syndicateArticle({
          slug: resultRow.slug,
          title: (fresh as any)?.title ?? data.title,
          excerpt: (fresh as any)?.excerpt ?? data.excerpt ?? null,
          coverImageUrl: (fresh as any)?.cover_image_url ?? null,
        });
      } catch (err) {
        console.warn(
          `[adminUpsertArticle] syndication failed for ${resultRow.id}:`,
          (err as Error).message,
        );
      }
    }

    return resultRow;
  });

export const adminPublishArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string; force?: boolean }) =>
    z.object({ id: z.string().uuid(), force: z.boolean().optional() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);


    const { data: row, error: loadErr } = await context.supabase
      .from("articles")
      .select(
        "id, slug, title, excerpt, cover_image_url, status, published_at, editor_pass_completed_at, quality_score",
      )
      .eq("id", data.id)
      .maybeSingle();
    if (loadErr) throw new Error(loadErr.message);
    if (!row) throw new Error("Article not found");

    if (row.status === "published" && !data.force) {
      return {
        ok: true,
        alreadyPublished: true,
        slug: row.slug,
        cover_image_url: row.cover_image_url,
        syndicated: false,
        warnings: [] as string[],
      };
    }


    const warnings: string[] = [];
    if (!row.editor_pass_completed_at) warnings.push("no-editor-pass");
    if (!row.cover_image_url) warnings.push("no-cover-image");

    // 1. Cover image (auto-fill if missing). Never block publish on failure.
    let coverUrl: string | null = row.cover_image_url ?? null;
    try {
      const { ensureCoverForArticle } = await import("./cover-image.server");
      const result = await ensureCoverForArticle(row.id);
      if (result?.url) coverUrl = result.url;
    } catch (err) {
      console.warn(
        `[adminPublishArticle] cover ensure failed for ${row.id}:`,
        (err as Error).message,
      );
    }

    // 2. Flip the row live. Preserve published_at if a backdated one exists.
    const finalPublishedAt = row.published_at ?? new Date().toISOString();
    const { error: updErr } = await context.supabase
      .from("articles")
      .update({ status: "published", published_at: finalPublishedAt })
      .eq("id", row.id);
    if (updErr) throw new Error(updErr.message);

    // 3. Syndicate. Never block publish on failure.
    let syndicated = false;
    try {
      const { syndicateArticle } = await import("./syndication.server");
      await syndicateArticle({
        slug: row.slug,
        title: row.title,
        excerpt: row.excerpt ?? null,
        coverImageUrl: coverUrl,
      });
      syndicated = true;
    } catch (err) {
      console.warn(
        `[adminPublishArticle] syndication failed for ${row.id}:`,
        (err as Error).message,
      );
    }

    return {
      ok: true,
      alreadyPublished: false,
      slug: row.slug,
      cover_image_url: coverUrl,
      syndicated,
      warnings,
    };
  });



export const adminDeleteArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("articles").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    return { isAdmin: !!data, userId: context.userId };
  });

/**
 * Safety net: any draft that has a past `published_at` is a data-shape bug
 * (the public site can't see it, the publisher won't schedule it). Promote
 * such rows to `published`, keeping their existing backdated timestamp so
 * they land in the archive, not at the top of the homepage.
 */
export const adminPromoteBackdatedDrafts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const nowIso = new Date().toISOString();
    const { data, error } = await context.supabase
      .from("articles")
      .update({ status: "published" })
      .eq("status", "draft")
      .not("published_at", "is", null)
      .lte("published_at", nowIso)
      .select("id");
    if (error) throw new Error(error.message);
    return { promoted: data?.length ?? 0 };
  });

